/* eslint-disable no-underscore-dangle */
import { CustomerInterface } from 'client/Interfaces/CustomerInterface'
import { PaymentMeanInterface } from 'client/Interfaces/Settings/PaymentMeanInterface'
import getFuzzyMatchScore from 'client/Other/functions/fuzzyMatch'
import dayjs, { Dayjs } from 'dayjs'
import _ from 'lodash'
import moment from 'moment'

import { AddProductInterface, ChangeDraftInterface, ChangeFieldsInterface } from '../actions/draft'
import { putProductToLine } from '../actions/products'
import { InvoicenumberResponseInterface } from '../api/invoiceNumber'
import { EInvoiceAddressInterface } from '../Global/EInvoicingData'
import { isInvoiceTypeWithMandatoryVatColumn, shouldHideVatColumn } from '../Global/InvoiceTypes'
import { CompanyInterface } from '../Interfaces/CompanyInterface'
import { ConfigInterface } from '../Interfaces/ConfigsInterface'
import {
  CharacterLimitInterface,
  DocumentNumberType,
  DocumentTypes,
  DraftInterface,
  InvoiceDocumentNumberType,
  InvoicerInterface,
} from '../Interfaces/Draft/DraftInterface'
import {
  Columns,
  DraftFieldsInterface,
  InvoiceNumberFieldInterface,
  paymentReferenceTypes,
} from '../Interfaces/Draft/FieldsInterface'
import { LineInterface } from '../Interfaces/Draft/LineInterface'
import { ExternalTaxRatesInterface } from '../Interfaces/ExternalVATRatesInterface'
import { EstimateInterface, InvoiceInterface } from '../Interfaces/Invoice/InvoiceInterface'
import { InvoiceTypeInterface } from '../Interfaces/InvoiceTypeInterface'
import { OrderDraftInterface } from '../Interfaces/OrderInterface'
import { SettingsInterface } from '../Interfaces/Settings/SettingsInterface'
import { StateInterface } from '../Interfaces/StateInterface'
import { getInvoiceTypes } from '../Other/Configs'
import { calculateDaysFromStartDate } from '../Other/functions/dateFunctions'
import { getRegionFromCountry } from '../Other/functions/externalTaxRateFunctions'
import {
  calculateValuesForDraft,
  calculateValuesForLine,
  isZeroVatInvoiceType,
} from '../Other/functions/invoiceLineFunctions'
import { formatNumber } from '../Other/functions/invoiceNumberFunctions'
import { isFieldVisible } from '../Other/functions/modelFunctions'
import { generateReferenceNumber, generateStructuredCommunication } from '../Other/functions/referenceNumberFunctions'
import { Immutable } from '../Other/Immutable'
import { ObjectNumber } from '../Other/ObjectNumber'
import { Type } from '../Other/Type'
import { ReducerBuilder } from './ReducerBuilder'

type StateType = DraftInterface | {}
type PayloadType =
  | CustomerInterface
  | AddProductInterface
  | LineInterface[]
  | InvoicerInterface
  | ChangeDraftInterface
  | ChangeFieldsInterface
  | DraftInterface
  | string[]
  | string
  | InvoiceDocumentNumberType
  | SettingsInterface
  | CompanyInterface
  | InvoiceInterface
  | EstimateInterface
  | OrderDraftInterface
  | InvoicenumberResponseInterface
  | DocumentNumberType
  | number
  | boolean

const INITIAL_STATE = {}

const ZERO_VAT_RATE_PREDEFINED_TAGS: Record<string, string> = {
  DE: 's_de_0',
  FR: 's_fr_0',
  NL: 's_nl_ex',
}

const NO_VAT_INVOICE_TYPES = ['SALES_VATNOLIABLE', 'SALES_VATLESS', 'SALES_REVERSE_VAT']

function _updateVatNumber(state: StateType, payload: string): StateType {
  if (state?.invoicer) {
    const newState = Immutable.copy(state) as DraftInterface
    newState.invoicer.vatNumber = payload
    return newState
  }
  return state
}

function _addProductToLine(state: StateType, payload: AddProductInterface): StateType {
  const newState: DraftInterface = Immutable.copy(state) as DraftInterface
  const { index, product } = payload
  if (newState.lines && newState.lines[index]) {
    newState.lines[index] = putProductToLine(newState.lines[index], product)
  }
  return newState
}

function _assignReceiver(state: StateType, payload: CustomerInterface): StateType {
  return { ...state, receiver: Immutable.copy(payload) }
}

function _changeDraftLines(state: DraftInterface, payload: LineInterface[]): StateType {
  const newState = Immutable.copy(state)
  const lines = Immutable.copy(payload)
  newState.isDirty = true
  let currentSubtotal = new ObjectNumber()
  let currentSubvat = new ObjectNumber()
  let currentSubnet = new ObjectNumber()
  const fields = newState.fields
  const invoiceTypeId = String(fields?.type?.value)
  const customerCountry = newState?.receiver?.address?.country
  const invoicerCountry = newState?.invoicer?.tradeName?.country
  const customerRegion = getRegionFromCountry(customerCountry, invoicerCountry)
  const isCustomerLocal = customerRegion === 'local'

  for (const line of lines) {
    if (Type.isInvoiceLine(line)) {
      calculateValuesForLine(line, fields, newState.document)
      currentSubtotal.add(line.grossTotal)
      currentSubvat.add(line.taxTotal)
      currentSubnet.add(line.netTotal)
      line.currency = fields.currency.value
    }

    if (Type.isInvoiceSubtotalLine(line)) {
      line.grossTotal = currentSubtotal.toNumber()
      line.taxTotal = currentSubvat.toNumber()
      line.netTotal = currentSubnet.toNumber()

      currentSubvat = new ObjectNumber()
      currentSubtotal = new ObjectNumber()
      currentSubnet = new ObjectNumber()
    }

    if (isZeroVatInvoiceType(invoiceTypeId) && (isCustomerLocal || !customerRegion)) {
      line.vatRateId = invoicerCountry && ZERO_VAT_RATE_PREDEFINED_TAGS[invoicerCountry]
      line.vatPct = 0
    }
  }

  newState.lines = lines
  return newState
}

function _changeDraft(state: DraftInterface, payload: ChangeDraftInterface) {
  const { draft, tradeNames } = payload
  if (
    state === null ||
    typeof state.invoicer === 'undefined' ||
    state.invoicer.tradeName._id !== draft.invoicer.tradeName._id
  ) {
    const { tradeName: oldTN } = draft.invoicer
    const newTN = tradeNames.find((tn) => tn._id === draft.invoicer.tradeName._id)
    draft.invoicer.tradeName = Object.assign({}, oldTN, newTN)
  }

  if (
    payload.draft.fields.columns.value.indexOf('vatPct') < 0 &&
    !isInvoiceTypeWithMandatoryVatColumn(payload?.invoiceType)
  ) {
    _clearTaxFromRows(payload.draft, payload.externalVATRates, payload.invoiceType)
  }
  return calculateValuesForDraft(draft)
}

function _clearTaxFromRows(
  draft: DraftInterface,
  externalVATRates?: ExternalTaxRatesInterface,
  invoiceType?: InvoiceTypeInterface,
): void {
  const customerCountry = draft?.receiver?.address?.country
  const invoicerCountry = draft?.invoicer?.tradeName?.country
  const customerRegion = getRegionFromCountry(customerCountry, invoicerCountry)
  const isCustomerLocal = customerRegion === 'local'

  if (Array.isArray(draft.lines)) {
    draft.lines.forEach((line) => {
      if (line.type && line.type === 'row') {
        line.vatPct = 0
        line.taxTotal = 0
        line.grossTotal = line.netTotal
      }
    })
  }

  if (
    invoiceType &&
    externalVATRates &&
    (isCustomerLocal || !customerRegion) &&
    NO_VAT_INVOICE_TYPES.includes(invoiceType.type)
  ) {
    const zeroVatRateTag = invoicerCountry && ZERO_VAT_RATE_PREDEFINED_TAGS[invoicerCountry]
    const taxRate = externalVATRates.taxRates.find((taxRate) => taxRate.predefinedTag === zeroVatRateTag)
    if (taxRate && taxRate.id) {
      draft.lines?.forEach((line) => {
        if (line.type === 'row') {
          line.vatRateId = taxRate.id
        }
      })
    }
  }
}

export function filterColumnsValueBasedOnInvoiceType({
  columnsValue,
  invoiceType,
}: {
  columnsValue: Columns[]
  invoiceType: InvoiceTypeInterface | {}
}): Columns[] {
  const newColumns = new Set<Columns>(columnsValue ?? [])
  if (shouldHideVatColumn(invoiceType) && newColumns.has('vatPct')) {
    newColumns.delete('vatPct')
  }
  return Array.from(newColumns)
}

function _changeFields(state: DraftInterface, payload: ChangeFieldsInterface): StateType {
  const { EXTRA_COLUMNS } = require('../components/invoiceEditor/InvoiceEditorController')
  const newState = Immutable.copy(state)
  // mark draft unsaved (dirty)
  newState.isDirty = true
  // update columns when invoiceType is changed
  const invoiceTypes = getInvoiceTypes(payload.configs, payload.country, state.document)
  const type = invoiceTypes.filter((t) => {
    return parseInt(t.id, 10) === payload.fields.type.value
  })[0]
  const timeZone = { name: 'timeZone', value: Intl.DateTimeFormat().resolvedOptions().timeZone, visible: true }
  newState.fields.timeZone = timeZone

  if (type) {
    if (payload.fields.type.value !== newState.fields.type.value) {
      /**
       * If changing invoice type from settings, filter columns based on invoice type
       */
      payload.fields.columns.value = filterColumnsValueBasedOnInvoiceType({
        columnsValue: type.fields,
        invoiceType: type,
      })

      // if taxPct is not visible, remove tax values and columns from draft
      if (payload.fields.columns.value.indexOf('vatPct') < 0) {
        payload.fields.columns.value = payload.fields.columns.value.filter((c) => {
          return c !== 'vatPct' && c !== 'taxTotal'
        })
        _clearTaxFromRows(newState, payload.externalVATRates, invoiceTypes[payload.fields.type.value])
      }
    } else {
      payload.fields.columns.value = payload.fields.columns.value.filter((col) => {
        if (col === 'grossTotal') {
          col = 'total'
        }
        return (
          _.includes(type.fields, col) ||
          (col !== 'taxTotal' && _.includes(EXTRA_COLUMNS, col)) ||
          (col === 'taxTotal' &&
            (_.includes(payload.fields.columns.value, 'vatPct') || isInvoiceTypeWithMandatoryVatColumn(type)))
        )
      })
      if (payload.fields.columns.value.indexOf('vatPct') < 0 && !isInvoiceTypeWithMandatoryVatColumn(type)) {
        _clearTaxFromRows(newState, payload.externalVATRates, invoiceTypes[payload.fields.type.value])
      }
    }
  }
  if (
    isFieldVisible('dueDate', payload.settings, newState.document) &&
    isFieldVisible('paymentTime', payload.settings, newState.document) &&
    payload.fields.issueDate &&
    payload.fields.issueDate.value
  ) {
    const now = dayjs(payload.fields.issueDate.value)
    const due = calculateDaysFromStartDate(now, payload.fields.paymentTime.value)
    payload.fields.dueDate.value = due.format()
  }

  if (!_.includes(payload.fields.columns.value, 'vatPct') && isInvoiceTypeWithMandatoryVatColumn(type)) {
    payload.fields.columns.value.push('vatPct')
  }

  // If language is changed and the title is unchanged, change title to default translation for the new language
  if (newState.fields?.locale?.value !== payload.fields?.locale?.value) {
    const oldLocale = newState.fields?.locale?.value
    const newLocale = payload.fields?.locale?.value
    let defaultTranslation
    let newTranslation
    const config: ConfigInterface = payload.configs
    switch (newState.document) {
      case 'invoice':
        defaultTranslation = config ? config.invoiceTranslations[oldLocale] : ''
        newTranslation = config ? config.invoiceTranslations[newLocale] : ''
        break
      case 'estimate':
        defaultTranslation = config ? config.offerTranslations[oldLocale] : ''
        newTranslation = config ? config.offerTranslations[newLocale] : ''
        break
      case 'creditnote': // not necessarily needed since user can't change credit note customer or language
        defaultTranslation = config ? config.creditTranslations[oldLocale] : ''
        newTranslation = config ? config.creditTranslations[newLocale] : ''
        break
    }
    if (newState.fields.title.value.toUpperCase() === defaultTranslation.toUpperCase()) {
      payload.fields.title.value = newTranslation.toUpperCase()
    }
  }

  newState.fields = payload.fields

  newState.lines.forEach((line) => {
    if (line.type === 'subtotal' && payload.fields.columns && payload.fields.columns.value) {
      line.showTaxTotal = payload.fields.columns.value.indexOf('taxTotal') >= 0
      line.showNetTotal = payload.fields.columns.value.indexOf('netTotal') >= 0
      line.showGrossTotal = payload.fields.columns.value.indexOf('grossTotal') >= 0
    }
    return line
  })

  // set receiver language based on what user choose
  if (newState.receiver && newState.receiver.language && payload.fields.locale) {
    newState.receiver.language = payload.fields.locale.value.split('_')[0]
  }

  if (newState.fields.discountEnabled.value && newState.fields.discountLocation.value !== 'perTotal') {
    if (newState.fields.columns.value.indexOf('discountPct') < 0) {
      newState.fields.columns.value.push('discountPct')
    }
  } else {
    newState.fields.columns.value = _.without(newState.fields.columns.value, 'discountPct')
  }
  return newState
}

function _setErrors(state: DraftInterface, payload: string[]) {
  const newState = Immutable.copy(state)
  newState.invalidFields = payload
  return newState
}

/*
  Intersection fn is used here so that existing errors are updated but no new are added.
  Complete replacement can be done with _setErrors above if so is the intention.
*/
function _updateErrors(state: DraftInterface, payload: string[]) {
  const newState = Immutable.copy(state)
  newState.invalidFields = _.intersection(newState.invalidFields, payload)
  return newState
}

function _updateInvoicer(state: DraftInterface, payload: InvoicerInterface) {
  const newState = Immutable.copy(state)
  newState.invoicer = payload
  return newState
}

// NOTE:
//  - When creating a new invoice the shape of the payload is InvoiceDocumentNumberType.
//  - When editing an invoice number in InvoiceNumberSequenceModal, the shape of the payload is DocumentNumberType
function _updateInvoiceNumber(state: DraftInterface, payload: DocumentNumberType | InvoiceDocumentNumberType) {
  const newState = Immutable.copy(state)
  const defaultInvoiceNumberField: InvoiceNumberFieldInterface = { name: 'invoiceNumber', visible: true, value: null }
  const invoiceNumber = (payload as DocumentNumberType).number || (payload as InvoiceDocumentNumberType).next || 1

  newState.fields = newState.fields || ({} as any)
  newState.fields.invoiceNumber = newState.fields.invoiceNumber || defaultInvoiceNumberField
  newState.fields.invoiceNumber.value = formatNumber(payload.prefix, Number(invoiceNumber))
  newState.invoiceNumber = { prefix: payload.prefix, number: invoiceNumber }
  return newState
}

function _updateEstimateNumber(state: DraftInterface, payload: InvoicenumberResponseInterface) {
  const newState = Immutable.copy(state)
  const newInvoiceNumber = payload.invoicenumber === false ? '1' : String(payload.invoicenumber)
  newState.fields = newState.fields || ({} as any)
  const defaultInvoiceNumberField: InvoiceNumberFieldInterface = { name: 'invoiceNumber', visible: true, value: null }
  newState.fields.invoiceNumber = {
    ...newState.fields.invoiceNumber,
    ...defaultInvoiceNumberField,
    value: newInvoiceNumber,
  }
  return newState
}

const isValidPM = (pm) => {
  const { fields } = pm
  if (!fields) {
    return false
  }
  return Object.keys(fields).some((f) => fields[f])
}

function _updateDraftStylesFromSettings(state: DraftInterface, payload: SettingsInterface) {
  const newState = Immutable.copy(state)
  const {
    document,
    fields: {
      template: { value: templateName },
    },
  } = newState
  const [, newTemplate] = templateName.split('.')
  if (!newTemplate) {
    console.error(`Invalid template ${templateName}`)
    return newState
  }
  const newStyles =
    (payload.styles[document] && payload.styles[document][newTemplate]) || payload.styles.invoice[newTemplate] || false
  if (newStyles) {
    newState.styles = newStyles
  } else {
    console.error(`Cannot find ${document} template ${newTemplate}`)
  }
  return newState
}

interface DraftFieldInterface {
  name: string
  visible?: boolean
  value?: number | string
}

const createFieldDataFromSettings = (settings: SettingsInterface, docType, fieldName) => {
  const isEstimate = docType === DocumentTypes.ESTIMATE
  const modelName = isEstimate ? 'models.estimate' : 'models.invoice.sales'
  const { fields } = settings.models.find((m) => m.name === modelName)
  const settingsField = fields.find((field) => field.name === fieldName)
  if (!settingsField) {
    return null
  }
  const { name, default: value, visible } = settingsField
  const res: DraftFieldInterface = {
    name,
    visible,
    ...(value ? { value } : {}),
  }
  if (name === 'interest') {
    res.value = Number(settingsField.default) || 0
  }
  return res
}

function _updateCustomerNumberFromSettings(state: DraftInterface, payload: SettingsInterface) {
  const newState = Immutable.copy(state)
  const field = 'customerNumber'
  const fieldData = createFieldDataFromSettings(payload, newState.document, field)
  newState.fields[field] = {
    name: field,
    visible: (fieldData && fieldData.visible) || false,
  }
  return newState
}

function _updatePaymentReferenceFromSettings(state: DraftInterface, payload: SettingsInterface) {
  const newState = Immutable.copy(state)
  const field = 'paymentReferenceType'
  const oldFieldData = newState.fields[field]
  const fieldData = createFieldDataFromSettings(payload, newState.document, field)
  if (fieldData) {
    const { visible, value: newValue } = fieldData
    newState.fields[field] = {
      name: field,
      value: oldFieldData && oldFieldData.value ? oldFieldData.value : newValue,
      visible,
    }
  }
  return newState
}

function _updateDraftFieldsFromSettings(state: DraftInterface, payload: SettingsInterface) {
  const newState = Immutable.copy(state)
  const isEdit = newState.editInvoice || false
  const now = new Date()
  const defaultValues = {
    referenceNumber: '',
    recipientReference: '',
    interest: 0,
    legalterms: '',
    dueDate: now.toISOString(),
    paymentTime: 30,
  }
  const fieldsWithValue = ['interest', 'legalterms', 'paymentReferenceType']
  const fieldsVisOnly = ['dueDate', 'paymentTime', 'referenceNumber', 'recipientReference', 'customerNumber']
  const fieldsToUpdate = [...fieldsWithValue, ...fieldsVisOnly]
  const useIfDefined = (v, d) => (v === null || v === undefined ? d : v)
  fieldsToUpdate.forEach((field) => {
    const fieldData = createFieldDataFromSettings(payload, newState.document, field)
    if (fieldData) {
      const oldValue = newState.fields[field]
        ? useIfDefined(newState.fields[field].value, defaultValues[field])
        : defaultValues[field]
      const { visible, value: newValue } = fieldData
      const value = fieldsVisOnly.includes(field) || (isEdit && field === 'interest') ? oldValue : newValue
      newState.fields[field] = {
        name: field,
        visible,
        value,
      }
    }
  })
  if (
    [DocumentTypes.INVOICE, DocumentTypes.CREDITNOTE].includes(newState.document) &&
    !isEdit &&
    newState.fields.paymentReferenceType &&
    [paymentReferenceTypes.SPY_FI, paymentReferenceTypes.SPY_BE].includes(newState.fields.paymentReferenceType.value)
  ) {
    newState.fields.referenceNumber.value =
      newState.fields.paymentReferenceType.value === paymentReferenceTypes.SPY_FI
        ? generateReferenceNumber(newState.fields.invoiceNumber.value)
        : generateStructuredCommunication()
  }
  return newState
}

export const getSettingsIbans = (settings) => {
  const settingsIban = Immutable.copy(settings.values.paymentMethods)
    .filter(isValidPM)
    .find(({ type }) => type === 'IBAN')
  const settingsIbans = []
  if (settingsIban) {
    settingsIbans.push(settingsIban.fields)
    ;(settingsIban.optionalIBANs || []).forEach((iban) => {
      settingsIbans.push(iban)
    })
  }
  return settingsIbans
}

export const getTopMatchingIbanFields = (currIBAN: PaymentMeanInterface, settings: SettingsInterface) => {
  // if there is only one IBAN return that, if there  are multiple, find the one with highest score above 0.85.
  // If none with score above 0.85, return default IBAN
  const matchThreshold = 0.85
  const settingsIBANs = getSettingsIbans(settings)
  const currIBANFields = currIBAN.fields
  let bestScoreIdx = 0
  let bestScore = 0
  settingsIBANs.forEach(({ iban, swift, bank }, idx) => {
    // iban value match score has twice the weight as the swift or bank.
    const score =
      (getFuzzyMatchScore(currIBANFields.iban, iban) * 2 + // iban has 2x weight
        getFuzzyMatchScore(currIBANFields.swift, swift) / 2 + // swift & bank have 1/2x weight
        getFuzzyMatchScore(currIBANFields.bank, bank) / 2) /
      3
    if (score > bestScore) {
      bestScore = score
      bestScoreIdx = idx
    }
  })
  return bestScore > matchThreshold ? settingsIBANs[bestScoreIdx] : settingsIBANs[0]
}

function _updateDraftCompanyFromSettings(state: DraftInterface, payload: SettingsInterface) {
  const newState = Immutable.copy(state)
  const rcs = payload.values.company.rcs || ''
  const legalStatus = payload.values.company.legalStatus || ''
  const firmenbuchregister = payload.values.company.firmenbuchregister || ''
  const { taxPrepaymentRegistered } = payload.values.company
  let paymentMeans = Immutable.copy(payload.values.paymentMethods)
    .filter(isValidPM)
    .map((pm) => _.pick(pm, ['type', 'fields']))
  if (newState.paymentMeans) {
    // replace by iban in the settings if the iban in the draft matches any of defalult & optionalIBANs
    const iban = newState.paymentMeans.find(({ type }) => type === 'IBAN') // IBAN in the draft
    const settingsIban = paymentMeans.find(({ type }) => type === 'IBAN')
    if (iban && settingsIban) {
      const settingsIbans = getSettingsIbans(payload)
      const matchingIbanFieldsFromSettings = settingsIbans.find((sIBAN) => sIBAN.iban === iban.fields.iban)
      // replace by matching iban from settings
      paymentMeans.find(({ type }) => type === 'IBAN')
      paymentMeans = [
        { ...iban, fields: matchingIbanFieldsFromSettings || iban.fields },
        ...paymentMeans.filter(({ type }) => type !== 'IBAN'),
      ]
    }
  }
  if (newState.document === DocumentTypes.INVOICE) {
    newState.paymentMeans = paymentMeans
  }
  const model = payload.models.find((m) => m.type === newState.document)
  const senderField = model.fields.find((f) => f.name === 'sender')
  const sender = senderField && senderField.default
  const tradeNames = payload.values.company.tradeNames
  const firstTN = tradeNames[0]
  const defaultTN = _.find(tradeNames, { default: true })
  const senderTN = _.find(tradeNames, { _id: sender })
  const draftTN = _.find(tradeNames, { _id: state.invoicer.tradeName._id })
  // are all there really necessary?
  const tradeName = draftTN || defaultTN || senderTN || firstTN
  // country is not saved in tradename, so pick that from the draft
  tradeName.country = state.invoicer.tradeName.country

  newState.invoicer.rcs = rcs
  newState.invoicer.legalStatus = legalStatus
  newState.invoicer.tradeName = tradeName
  delete newState.invoicer.firmenbuchregister
  if (firmenbuchregister) {
    newState.invoicer.firmenbuchregister = firmenbuchregister
  }
  if (typeof taxPrepaymentRegistered === 'boolean') {
    newState.invoicer.taxPrepaymentRegistered = taxPrepaymentRegistered
  } else {
    delete newState.invoicer.taxPrepaymentRegistered
  }
  return newState
}

function _updateDraftCompanyFromCompany(state: DraftInterface, payload: CompanyInterface) {
  const newState = Immutable.copy(state)
  const vatNumber = payload.vatNumber
  const businessId = payload.businessId
  newState.invoicer.vatNumber = vatNumber
  newState.invoicer.businessId = businessId
  newState.invoicer.tradeName.country = payload.country
  return newState
}

function _replacePaymentMethodsFromSettings(state: DraftInterface, payload: SettingsInterface) {
  const newState = Immutable.copy(state)
  newState.paymentMeans = Immutable.copy(payload.values.paymentMethods)
  return newState
}

const updateInvoiceColumnsFromInvoiceType = (state: DraftFieldsInterface) => {
  const newState = Immutable.copy(state)
  return newState
}

function _updateDraftCustomerFromCustomers(state: DraftInterface, payload: CustomerInterface[]) {
  const newCustomer = payload?.find((c) => c.id === state?.receiver?.id)
  const newState = Immutable.copy(state)
  if (newCustomer) {
    newState.receiver = Immutable.copy(newCustomer)
  } else {
    newState.receiver = null
  }
  return newState
}

function _updateDraftCustomer(state: DraftInterface, payload: CustomerInterface) {
  const newState = Immutable.copy(state)
  if (newState.document === DocumentTypes.RECEIPT) {
    newState.customer = payload === null ? null : Immutable.copy(payload)
  } else {
    newState.receiver = payload === null ? null : Immutable.copy(payload)
  }
  return newState
}

function _getValidationFields(state: DraftFieldInterface, payload: EInvoiceAddressInterface) {
  const newState = Immutable.copy(state)
  newState.validationFields = payload
  return newState
}

function _updateReferenceNumber(state: DraftInterface, payload: string) {
  const newState = Immutable.copy(state)
  newState.fields.referenceNumber = newState.fields.referenceNumber || { name: 'referenceNumber', value: '' }
  newState.fields.referenceNumber.value = payload
  return newState
}

function updateEInvoiceSending(state: DraftInterface, payload: boolean = false) {
  const newState = Immutable.copy(state)
  newState.eInvoiceSending = payload
  return newState
}

function updateCharacterLimit(state: DraftInterface, payload: CharacterLimitInterface) {
  const newState = Immutable.copy(state)
  newState.characterLimitReached = payload
  return newState
}

function editInvoice(state: DraftInterface, payload: number): StateType {
  const newState = Immutable.copy(state)
  newState.editInvoice = true
  newState.invoiceId = payload
  return newState
}

function editEstimate(state: DraftInterface, payload: number): StateType {
  const newState = Immutable.copy(state)
  newState.editInvoice = true
  newState.estimateId = payload
  return newState
}

function saveInvoice(state: DraftInterface, payload: number): StateType {
  const newState = Immutable.copy(state)
  delete newState.isDirty
  return newState
}

function saveEstimate(state: DraftInterface, payload: number): StateType {
  const newState = Immutable.copy(state)
  delete newState.isDirty
  return newState
}

function getInvoice(state: DraftInterface, payload: InvoiceInterface): StateType {
  return payload.invoicedocument
}

function getEstimate(state: DraftInterface, payload: EstimateInterface): StateType {
  return payload.estimatedocument
}

function copyInvoice(state: DraftInterface, payload: InvoiceInterface): StateType {
  const newState = Immutable.copy(payload.invoicedocument)
  newState.id = 0
  const now = dayjs()
  newState.fields.issueDate.value = now.toISOString()
  !newState.fields.paymentTime &&
    (newState.fields.paymentTime = {
      name: 'paymentTime',
      value: 30,
      visible: true,
    })
  newState.fields.referenceNumber && (newState.fields.referenceNumber.value = '')
  const paymentTime = newState.fields.paymentTime.value
  newState.fields.dueDate.value = calculateDaysFromStartDate(now, paymentTime).toISOString()
  return newState
}

function copyEstimate(state: DraftInterface, payload: EstimateInterface): StateType {
  const newState = Immutable.copy(payload.estimatedocument)
  newState.id = 0
  newState.fields.issueDate.value = moment().toISOString()
  newState.fields.dueDate.value = moment().toISOString()

  return newState
}

function copyDraft(state: DraftInterface, payload: DraftInterface): StateType {
  const newState = Immutable.copy(payload)
  newState.id = 0
  newState.fields.issueDate.value = moment().toISOString()
  return newState
}

function getDraftPreview(state: DraftInterface, payload: string[]): StateType {
  const newState = Immutable.copy(state)
  newState.preview = payload
  return newState
}

function updateOrder(state: DraftInterface, payload: OrderDraftInterface): StateType {
  const newState = Immutable.copy(state)
  if (payload === null) {
    delete newState.order
  } else {
    newState.order = payload
  }
  return newState
}

function deleteOrder(state: DraftInterface): StateType {
  const newState = Immutable.copy(state)
  delete newState.order
  return newState
}

const copyPayload = (state: StateType, payload: DraftInterface) => payload

const draftReducers = {
  ASSIGN_RECEIVER: _assignReceiver,
  ADD_PRODUCT_LINE: _addProductToLine,
  CHANGE_LINES: _changeDraftLines,
  CHANGE_DRAFT: _changeDraft,
  CHANGE_FIELDS: _changeFields,
  CREATE_INVOICE_DRAFT: copyPayload,
  UPDATE_INVOICE_DRAFT: copyPayload,
  CREATE_ESTIMATE_DRAFT: copyPayload,
  UPDATE_ESTIMATE_DRAFT: copyPayload,
  DRAFT_VALIDATION_ERROR: _setErrors,
  UPDATE_DRAFT_VALIDATION_ERROR: _updateErrors,
  CLEAR_DRAFT: (state: StateType, payload: undefined) => null,
  DRAFT_CHANGE_VAT_NUMBER: _updateVatNumber,
  UPDATE_INVOICER: _updateInvoicer,
  UPDATE_INVOICE_NUMBER: _updateInvoiceNumber,
  UPDATE_REFERENCE_NUMBER: _updateReferenceNumber,
  UPDATE_EINVOICE_SENDING: updateEInvoiceSending,
  UPDATE_CHARACTER_LIMIT: updateCharacterLimit,
  UPDATE_PAYMENT_REFERENCE_TYPE_FROM_SETTINGS: _updatePaymentReferenceFromSettings,
  UPDATE_CUSTOMER_NUMBER_FROM_SETTINGS: _updateCustomerNumberFromSettings,
  UPDATE_DRAFT_CUSTOMER_FROM_CUSTOMERS: _updateDraftCustomerFromCustomers,
  REPLACE_PAYMENT_METHODS_FROM_SETTINGS: _replacePaymentMethodsFromSettings,
  UPDATE_INVOICE_COLUMNS_FROM_INVOICE_TYPE: updateInvoiceColumnsFromInvoiceType,
  GET_VALIDATION_FIELDS: _getValidationFields,
}

const estimateReducers = {
  UPDATE_ESTIMATE_NUMBER: _updateEstimateNumber,
  UPDATE_DRAFT_COMPANY_FROM_SETTINGS: _updateDraftCompanyFromSettings,
  UPDATE_DRAFT_FIELDS_FROM_SETTINGS: _updateDraftFieldsFromSettings,
  UPDATE_DRAFT_STYLES_FROM_SETTINGS: _updateDraftStylesFromSettings,
  UPDATE_DRAFT_COMPANY_FROM_COMPANY: _updateDraftCompanyFromCompany,
  UPDATE_DRAFT_CUSTOMER: _updateDraftCustomer,
  LOAD_DRAFT: (state: StateType, payload: DraftInterface) => payload,
  COPY_DRAFT: copyDraft,
  PREVIEW_DRAFT: getDraftPreview,
  UPDATE_ORDER: updateOrder,
  DELETE_ORDER: deleteOrder,
  EDIT_INVOICE: editInvoice,
  EDIT_ESTIMATE: editEstimate,
  SAVE_INVOICE: saveInvoice,
  SAVE_ESTIMATE: saveEstimate,
  GET_INVOICE: getInvoice,
  GET_ESTIMATE: getEstimate,
  COPY_INVOICE: copyInvoice,
  COPY_ESTIMATE: copyEstimate,
}

const actionsMap = { ...draftReducers, ...estimateReducers }
const reducer = new ReducerBuilder<StateType, PayloadType>(INITIAL_STATE)
Object.keys(actionsMap).forEach((key) => {
  reducer.mapAction(key, actionsMap[key])
})
export default reducer.build()

// Selectors
export const getDraftSelector = (state: StateInterface) => state.draft
